import { WindowConfiguration } from './window-configuration';
import { DoorActiveConfiguration } from './door-active-configuration';
import { HardwareVisualization } from '@icc/construction-elements/types';
import { IccAccessoryColor, IccColor } from '@icc/construction-elements/types';
import { AppConfig } from '@icc/shared';
import { DoorModelGroup } from '../api/door-model-group.dto';
import { DoorModel } from '../api/door-model.dto';
import { Components } from '../elements/components';
import { HandlesType } from '../elements/handle';

export class DoorConfiguration extends WindowConfiguration {
    type: 'door' = 'door';

    height = 2000;
    width = 1000;

    model: DoorModel = null;
    modelOptions: any;
    modelGroup: DoorModelGroup = null;
    sashesType: 'Fix' | 'FixSash' | 'Func' | 'Mix' = 'Func';
    ownedSashesTypes = {
        window: false,
        doorActive: true,
        doorPassive: false,
        doorTopLight: false,
        doorLeftLight: false,
        doorRightLight: false
    };
    handleType: HandlesType;
    doorHardware: {
        doorHandleAdjustment: null | number,
        type: null | number,
        set: null | 'leverPull' | 'doubleLever' | 'leverKnob' | 'doublePull' | 'innerLever' | 'leverRecessedPull' | 'mixedLever',
        setName: null | string,
        pull: null | number,
        pullColor: null | number,
        pullColorIsDefault: boolean,
        pullColorRelatedToPlate: boolean;
        electronicLock: {
            id: null | number,
            color: null | number,
            colorIsDefault: boolean,
        },
        beaconProximity: boolean,
        color: null | number,
        colorIsDefault: boolean,
        plateType: null | number,
        doorViewer: null | number,
        doorViewerColor: null | number;
        doorViewerColorIsDefault: boolean;
        doorViewerPunching: boolean,
        plates: {
            mainLock: null | number,
            additionalLock: null | number,
            color: null | number,
            colorIsDefault?: boolean,
        },
        outerPlates: {
            mainLock: null | number,
            additionalLock: null | number,
            color: null | number,
            colorIsDefault?: boolean,
        },
        cylinders: {
            mainLock: null | number,
            additionalLock: null | number,
            system: null | number,
            color: null | number,
            mainLockColor: null | number,
            additionalLockColor: null | number,
            mainLockColorIsDefault: boolean,
            additionalLockColorIsDefault: boolean,
            isKeyAvailableForMainLock: null | boolean,
            maxKeyQuantityForMainLock: null | number,
            keyQuantityForMainLock: null | number,
            keyPriceForMainLock: null | number,
            isKeyAvailableForAdditionalLock: null | boolean,
            maxKeyQuantityForAdditionalLock: null | number,
            keyQuantityForAdditionalLock: null | number,
            keyPriceForAdditionalLock: null | number,
        },
        decorativeHingeCovers: null | number,
        decorativeHingeCoversColor: null | number,
        decorativeHingeCoversColorIsDefault: boolean,
        decorativeHingeCoversColorRelatedToPlate: boolean,
        visualizations: {
            pull: HardwareVisualization | null,
            doorViewer: HardwareVisualization | null,
            mainLockPlate: HardwareVisualization | null,
            outerMainLockPlate: HardwareVisualization | null,
            additionalLockPlate: HardwareVisualization | null,
            outerAdditionalLockPlate: HardwareVisualization | null,
            mainLockCylinder: HardwareVisualization | null,
            additionalLockCylinder: HardwareVisualization | null,
            decorativeHingeCovers: HardwareVisualization | null
            recessedPull: HardwareVisualization | null
        },
        recessedPull: null | number;
        recessedPullColor: {
            colorSource: string,
            color: IccAccessoryColor | IccColor,
            isDefault: boolean,
            price: number,
            list_price: number
        } | null;
    } = {
        doorHandleAdjustment: null,
        type: null,
        set: null,
        setName: null,
        pull: null,
        pullColor: null,
        pullColorIsDefault: true,
        pullColorRelatedToPlate: null,
        electronicLock: {
            id: null,
            color: null,
            colorIsDefault: true,
        },
        beaconProximity: false,
        color: null,
        colorIsDefault: true,
        plateType: null,
        doorViewer: null,
        doorViewerColor: null,
        doorViewerColorIsDefault: true,
        doorViewerPunching: false,
        plates: {
            mainLock: null,
            additionalLock: null,
            color: null,
            colorIsDefault: true,
        },
        outerPlates: {
            mainLock: null,
            additionalLock: null,
            color: null,
            colorIsDefault: true,
        },
        cylinders: {
            mainLock: null,
            additionalLock: null,
            system: null,
            color: null,
            mainLockColor: null,
            additionalLockColor: null,
            mainLockColorIsDefault: true,
            additionalLockColorIsDefault: true,
            isKeyAvailableForMainLock: null,
            maxKeyQuantityForMainLock: null,
            keyQuantityForMainLock: null,
            keyPriceForMainLock: null,
            isKeyAvailableForAdditionalLock: null,
            maxKeyQuantityForAdditionalLock: null,
            keyQuantityForAdditionalLock: null,
            keyPriceForAdditionalLock: null,
        },
        decorativeHingeCovers: null,
        decorativeHingeCoversColor: null,
        decorativeHingeCoversColorIsDefault: true,
        decorativeHingeCoversColorRelatedToPlate: false,
        visualizations: {
            pull: null,
            doorViewer: null,
            mainLockPlate: null,
            outerMainLockPlate: null,
            additionalLockPlate: null,
            outerAdditionalLockPlate: null,
            mainLockCylinder: null,
            additionalLockCylinder: null,
            decorativeHingeCovers: null,
            recessedPull: null
        },
        recessedPull: null,
        recessedPullColor: null
    };

    factors = {
        thermalTranssmitance: {
            door: null,
            doorLights: {
                left: null,
                right: null,
                top: null,
                total: null
            },
            construction: null
        }
    };
    slat: {
        sashId: number,
        id: number,
        name: string,
        index_no: string,
        extId: string,
        image: string,
        count: number,
        colorName: string,
        width: number,
        space: number,
        bottomOffset: number,
        topOffset: number,
        innerHingeOffset: number,
        outerHingeOffset: number,
        outerOpen: boolean
        colorImg: string,
        color: string,
        slatColor: IccColor
    } | null = null;
    components: Components;

    constructor(
        configuration?,
        dataRequiredToUpdate?: any,
        conversion = true,
        configuratorsDataService?,
        langCode = configuration?.langCode || 'pl',
        config: AppConfig = null
    ) {
        super(configuration, dataRequiredToUpdate, true, configuratorsDataService, langCode, config);

        if (DoorConfiguration.is(configuration) && conversion) {
            this.runConversion(configuration, dataRequiredToUpdate);
        } else if (DoorActiveConfiguration.is(configuration)) {
            if (configuration.Model && configuration.Model.id) {
                this.model = {
                    id: configuration.Model.id,
                    name: configuration.Model.name,
                    standardFillings: configuration.Model.standard_fills,
                    indexNo: configuration.Model.index_no,
                    fillingsCategoryId: configuration.Model.fillings_category_id,
                    integratedPull: configuration.Model.integrated_pull
                };
            }

            if (configuration.ModelOptions) {
                this.modelOptions = configuration.ModelOptions;
            }

           let doorFrameAndSashShortening = 0
            if(this.doorSizes.shortening > 0) {
                doorFrameAndSashShortening = Math.abs(this.doorSizes.shortening - this.doorSizes.frameShortening);
            }
            this.height = configuration.Height + doorFrameAndSashShortening;
            ([
                'height',
                'h1',
                'h2',
                'h3',
            ] as const).forEach((field) => {
                if (this.shape[field] != null && this.shape[field] !== 0) {
                    this.shape[field] += Number(doorFrameAndSashShortening);
                }
            });
            this.sashesType = configuration.SashesType;
            this.width = configuration.Width;
            this.ownedSashesTypes = configuration.OwnedSashesTypes;
            this.doorHardware = configuration.doorHardware;
            this.dictionary.doorHardware = configuration.doorHardwareDictionary.doorHardware;
            this.dictionary.hardwareSystems = configuration.doorHardwareDictionary.hardwareSystems;
            this.dictionary.accessoryColors = {...this.dictionary.accessoryColors, ...configuration.doorHardwareDictionary.accessoryColors};
            this.factors = configuration.Factors;
            this.components = configuration.Components;
            this.setSlat(configuration)
        }
        delete this.configuratorsDataService;
        delete this.config;
    }

    public static is(configuration): configuration is DoorConfiguration {
        return (
            configuration instanceof DoorConfiguration
            || (configuration.$version && !configuration.active && configuration.type === 'door')
        );
    }

    private setSlat(configuration) {
        const activeSash = configuration.Sashes.find(s => s.type && (s.type.type === 'DRA' || s.type.type === 'DOA'));
        if(activeSash && configuration.Slat) {
            this.slat = {
                sashId: activeSash.id,
                id: configuration.Slat.id,
                name: configuration.Slat.name,
                index_no: configuration.Slat.indexNo,
                extId: configuration.Slat.extId,
                image: configuration.Slat.image,
                count: configuration.Slat.count,
                colorName: configuration.SlatColor.name,
                width: configuration.Slat.width,
                space: configuration.Slat.space,
                bottomOffset: configuration.Slat.bottomOffset,
                topOffset: configuration.Slat.topOffset,
                innerHingeOffset: configuration.Slat.innerHingeOffset,
                outerHingeOffset: configuration.Slat.outerHingeOffset,
                outerOpen: configuration.type.out_open,
                colorImg: configuration.SlatColor.colorImg,
                color: configuration.SlatColor.color,
                slatColor: configuration.SlatColor
            }
        }
    }
}
